'use client';

import type React from 'react';
import { useState, useEffect } from 'react';
import { Buttons } from '../../../../components/ui/button';
import { Badge } from '../../../../components/ui/badge';
import svgPaths from '../../../../imports/svg-zldmzd5udy';
import svgPaths2 from '../../../../imports/svg-knollny4nu';
import {
  CustomCoinIcon,
  CustomHomeIcon,
  CustomMapIcon,
  CustomRankingIcon,
  CustomSettingsIcon,
} from '../../../../components/ui/CustomIcons';
// import imgBackground from "/images/background.png";
import { useColor } from '../../../../components/ui/ColorContext';
import ColorPicker from '../../../../components/ui/ColorPicker';
import { Slider } from '../../../../components/ui/slider';
import {
  Dialog,
  DialogContent,
  DialogTrigger,
} from '../../../../components/ui/dialog';

// Helper function to convert hex to RGB
const hexToRgb = (hex: string): string => {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  if (!result) return '0, 255, 187'; // fallback to default green
  const r = Number.parseInt(result[1], 16);
  const g = Number.parseInt(result[2], 16);
  const b = Number.parseInt(result[3], 16);
  return `${r}, ${g}, ${b}`;
};

interface LoadingScreenProps {
  onNavigate?: (direction: 'left' | 'right') => void;
  progress: number;
  isStoryScreen?: boolean;
}

const LoadingGroup1: React.FC<{ delay: number; progress: number }> = ({
  delay,
  progress,
}) => {
  const { primaryColor } = useColor();

  // Total number of bars - much fewer for cleaner look
  const totalBars = 45;
  const barWidth = 4;
  const barSpacing = 8; // 4px gap between bars
  const barHeight = 20;
  const totalWidth = totalBars * barSpacing;

  // Calculate how many bars should be filled
  const filledBars = Math.floor((progress / 100) * totalBars);

  // Generate bars with gaps
  const bars = [];
  for (let i = 0; i < totalBars; i++) {
    const isFilled = i < filledBars;

    bars.push(
      <rect
        key={i}
        x={20 + i * barSpacing} // Start after the left pole
        y={5}
        width={barWidth}
        height={barHeight}
        fill={primaryColor}
        opacity={isFilled ? 1 : 0.2}
        style={{
          filter: isFilled ? `drop-shadow(0 0 2px ${primaryColor})` : 'none',
        }}
      />,
    );
  }

  // Progress marker position
  const markerX = 20 + (progress / 100) * (totalBars * barSpacing);

  return (
    <div
      className="absolute bottom-[82.93%] left-[38.285%] right-[35.7%] top-[16.093%] transition-opacity duration-500"
      style={{
        opacity: progress > 0 ? 1 : 0,
        animationDelay: `${delay}ms`,
      }}
    >
      <div className="flex h-full w-full items-center justify-center">
        <svg
          className="block h-8 w-full"
          viewBox={`0 0 ${totalWidth + 40} 30`}
          preserveAspectRatio="xMidYMid meet"
        >
          {/* Left pole */}
          <rect
            x={10}
            y={0}
            width={3}
            height={30}
            fill={primaryColor}
            opacity={0.9}
          />

          {/* Right pole */}
          <rect
            x={totalWidth + 27}
            y={0}
            width={3}
            height={30}
            fill={primaryColor}
            opacity={0.9}
          />

          {/* All bars */}
          {bars}

          {/* Top triangle marker */}
          {progress > 0 && progress < 100 && (
            <>
              <polygon
                points={`${markerX},0 ${markerX - 4},8 ${markerX + 4},8`}
                fill={primaryColor}
                opacity={0.9}
              />

              <polygon
                points={`${markerX},30 ${markerX - 4},22 ${markerX + 4},22`}
                fill={primaryColor}
                opacity={0.9}
              />
            </>
          )}
        </svg>
      </div>
    </div>
  );
};

const LoadingGroup2: React.FC<{ delay: number }> = ({ delay }) => {
  const { primaryColor } = useColor();

  return (
    <div
      className="absolute bottom-[92.466%] left-[37.216%] right-[46.933%] top-[4.795%] animate-pulse opacity-0"
      style={{
        animationDelay: `${delay}ms`,
        animationDuration: '2000ms',
        animationFillMode: 'forwards',
        animationIterationCount: 'infinite',
      }}
    >
      <div className="h-[0.717rem] w-[13.736rem] flex-none rotate-[180deg] scale-y-[-100%]">
        <svg
          className="block h-full w-full"
          fill="none"
          preserveAspectRatio="none"
          viewBox="0 0 220 12"
        >
          <g id="Group 5">
            <path d={svgPaths.p278cb700} fill={primaryColor} id="Vector" />
            <path d={svgPaths.pd21ba80} fill={primaryColor} id="Vector_2" />
            <path d={svgPaths.p26c28270} fill={primaryColor} id="Vector_3" />
            <path
              clipRule="evenodd"
              d={svgPaths.p12598d00}
              fill={primaryColor}
              fillRule="evenodd"
              id="Vector_4"
            />
            <path d={svgPaths.p18b3700} fill={primaryColor} id="Vector_5" />
            <path
              clipRule="evenodd"
              d={svgPaths.p26c87470}
              fill={primaryColor}
              fillRule="evenodd"
              id="Vector_6"
            />
            <path d={svgPaths.p1431fff0} fill={primaryColor} id="Vector_7" />
            <path
              clipRule="evenodd"
              d={svgPaths.p2f5c5700}
              fill={primaryColor}
              fillRule="evenodd"
              id="Vector_8"
            />
            <path d={svgPaths.p21ed0a00} fill={primaryColor} id="Vector_9" />
            <path
              clipRule="evenodd"
              d={svgPaths.p3f128100}
              fill={primaryColor}
              fillRule="evenodd"
              id="Vector_10"
            />
            <path d={svgPaths.p16045500} fill={primaryColor} id="Vector_11" />
            <path
              clipRule="evenodd"
              d={svgPaths.p225acc00}
              fill={primaryColor}
              fillRule="evenodd"
              id="Vector_12"
            />
            <path d={svgPaths.p3e1d0700} fill={primaryColor} id="Vector_13" />
            <path
              clipRule="evenodd"
              d={svgPaths.p1f42d980}
              fill={primaryColor}
              fillRule="evenodd"
              id="Vector_14"
            />
          </g>
        </svg>
      </div>
    </div>
  );
};

const LoadingGroup3: React.FC<{ delay: number }> = ({ delay }) => {
  const { primaryColor } = useColor();

  return (
    <div
      className="absolute bottom-[92.466%] left-[48.932%] right-[35.355%] top-[4.795%] animate-pulse opacity-0"
      style={{
        animationDelay: `${delay}ms`,
        animationDuration: '2000ms',
        animationFillMode: 'forwards',
        animationIterationCount: 'infinite',
      }}
    >
      <svg
        className="block h-full w-full"
        fill="none"
        preserveAspectRatio="none"
        viewBox="0 0 218 12"
      >
        <g id="Group 8">
          <path d={svgPaths.p28b7200} fill={primaryColor} id="Vector" />
          <path d={svgPaths.p3ca7f500} fill={primaryColor} id="Vector_2" />
          <path d={svgPaths.p36492900} fill={primaryColor} id="Vector_3" />
          <path
            clipRule="evenodd"
            d={svgPaths.pe001100}
            fill={primaryColor}
            fillRule="evenodd"
            id="Vector_4"
          />
          <path d={svgPaths.p29deb4c0} fill={primaryColor} id="Vector_5" />
          <path
            clipRule="evenodd"
            d={svgPaths.p1d6bbc00}
            fill={primaryColor}
            fillRule="evenodd"
            id="Vector_6"
          />
          <path d={svgPaths.p201bc900} fill={primaryColor} id="Vector_7" />
          <path
            clipRule="evenodd"
            d={svgPaths.p21e00400}
            fill={primaryColor}
            fillRule="evenodd"
            id="Vector_8"
          />
          <path d={svgPaths.p3e47600} fill={primaryColor} id="Vector_9" />
          <path
            clipRule="evenodd"
            d={svgPaths.p397df280}
            fill={primaryColor}
            fillRule="evenodd"
            id="Vector_10"
          />
          <path d={svgPaths.p2eadec00} fill={primaryColor} id="Vector_11" />
          <path
            clipRule="evenodd"
            d={svgPaths.pab94580}
            fill={primaryColor}
            fillRule="evenodd"
            id="Vector_12"
          />
          <path d={svgPaths.p2bd0a300} fill={primaryColor} id="Vector_13" />
          <path
            clipRule="evenodd"
            d={svgPaths.p4bfd40}
            fill={primaryColor}
            fillRule="evenodd"
            id="Vector_14"
          />
        </g>
      </svg>
    </div>
  );
};

const LoadingGroup4: React.FC<{ delay: number }> = ({ delay }) => {
  const { primaryColor } = useColor();

  return (
    <div
      className="absolute bottom-[65.069%] left-[52.998%] right-[31.013%] top-[32.192%] animate-pulse opacity-0"
      style={{
        animationDelay: `${delay}ms`,
        animationDuration: '2000ms',
        animationFillMode: 'forwards',
        animationIterationCount: 'infinite',
      }}
    >
      <svg
        className="block h-full w-full"
        fill="none"
        preserveAspectRatio="none"
        viewBox="0 0 222 12"
      >
        <g id="Group 6">
          <path d={svgPaths.p37182400} fill={primaryColor} id="Vector" />
          <path d={svgPaths.p34c0dac0} fill={primaryColor} id="Vector_2" />
          <path d={svgPaths.p15313100} fill={primaryColor} id="Vector_3" />
          <path
            clipRule="evenodd"
            d={svgPaths.p229725f0}
            fill={primaryColor}
            fillRule="evenodd"
            id="Vector_4"
          />
          <path d={svgPaths.p3f158d00} fill={primaryColor} id="Vector_5" />
          <path
            clipRule="evenodd"
            d={svgPaths.p2b26b900}
            fill={primaryColor}
            fillRule="evenodd"
            id="Vector_6"
          />
          <path d={svgPaths.p23d6e480} fill={primaryColor} id="Vector_7" />
          <path
            clipRule="evenodd"
            d={svgPaths.p2187db00}
            fill={primaryColor}
            fillRule="evenodd"
            id="Vector_8"
          />
          <path d={svgPaths.p51a5b00} fill={primaryColor} id="Vector_9" />
          <path
            clipRule="evenodd"
            d={svgPaths.p116dab80}
            fill={primaryColor}
            fillRule="evenodd"
            id="Vector_10"
          />
          <path d={svgPaths.p33a4e300} fill={primaryColor} id="Vector_11" />
          <path
            clipRule="evenodd"
            d={svgPaths.p4784c00}
            fill={primaryColor}
            fillRule="evenodd"
            id="Vector_12"
          />
          <path d={svgPaths.p12411a60} fill={primaryColor} id="Vector_13" />
          <path
            clipRule="evenodd"
            d={svgPaths.p1eef5400}
            fill={primaryColor}
            fillRule="evenodd"
            id="Vector_14"
          />
        </g>
      </svg>
    </div>
  );
};

const LoadingGroup5: React.FC<{ delay: number }> = ({ delay }) => {
  const { primaryColor } = useColor();

  return (
    <div
      className="absolute bottom-[65.068%] left-[34.114%] right-[46.933%] top-[32.192%] animate-pulse opacity-0"
      style={{
        animationDelay: `${delay}ms`,
        animationDuration: '2000ms',
        animationFillMode: 'forwards',
        animationIterationCount: 'infinite',
      }}
    >
      <div className="h-[0.717rem] w-[16.423rem] flex-none rotate-[180deg] scale-y-[-100%]">
        <svg
          className="w-ful block h-full"
          fill="none"
          preserveAspectRatio="none"
          viewBox="0 0 263 12"
        >
          <g id="Group 7">
            <path d={svgPaths.p3d1cf700} fill={primaryColor} id="Vector" />
            <path d={svgPaths.p26d50d00} fill={primaryColor} id="Vector_2" />
            <path d={svgPaths.p32930a00} fill={primaryColor} id="Vector_3" />
            <path
              clipRule="evenodd"
              d={svgPaths.p172bc300}
              fill={primaryColor}
              fillRule="evenodd"
              id="Vector_4"
            />
            <path d={svgPaths.pbd15400} fill={primaryColor} id="Vector_5" />
            <path
              clipRule="evenodd"
              d={svgPaths.p37d9b380}
              fill={primaryColor}
              fillRule="evenodd"
              id="Vector_6"
            />
            <path d={svgPaths.pf63a480} fill={primaryColor} id="Vector_7" />
            <path
              clipRule="evenodd"
              d={svgPaths.pbfb9100}
              fill={primaryColor}
              fillRule="evenodd"
              id="Vector_8"
            />
            <path d={svgPaths.p1d656d00} fill={primaryColor} id="Vector_9" />
            <path
              clipRule="evenodd"
              d={svgPaths.pc198900}
              fill={primaryColor}
              fillRule="evenodd"
              id="Vector_10"
            />
            <path d={svgPaths.p3d79de80} fill={primaryColor} id="Vector_11" />
            <path
              clipRule="evenodd"
              d={svgPaths.p26321980}
              fill={primaryColor}
              fillRule="evenodd"
              id="Vector_12"
            />
            <path d={svgPaths.p1d8c8580} fill={primaryColor} id="Vector_13" />
            <path
              clipRule="evenodd"
              d={svgPaths.p2a22e100}
              fill={primaryColor}
              fillRule="evenodd"
              id="Vector_14"
            />
          </g>
        </svg>
      </div>
    </div>
  );
};

const LoadingScreen: React.FC<LoadingScreenProps> = ({
  onNavigate,
  progress,
  isStoryScreen,
}) => {
  const { primaryColor } = useColor();

  return (
    <div
      className="relative h-screen w-full overflow-hidden bg-black/90 bg-cover bg-center bg-no-repeat"
      //   style={{ backgroundImage: `url('${imgBackground}')` }}
    >
      <div className="relative flex h-full w-full px-4 sm:px-6 lg:px-8">
        {/* Top Navigation Bar */}
        <div className="absolute left-0 right-0 top-0 flex h-[5.5rem] items-center justify-between bg-cover bg-center bg-no-repeat px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-6"></div>
          <div className="flex items-center gap-4">
            <Dialog>
              <DialogTrigger asChild>
                <div className="translate-y-1"></div>
              </DialogTrigger>
              <DialogContent
                className="rounded-[2rem] border-none px-7 py-6 shadow-lg sm:max-w-2xl"
                style={{
                  background: `linear-gradient(151.477deg, rgb(0,0,0) 17.606%, rgba(${hexToRgb(
                    primaryColor,
                  )}, 0.6) 218.68%)`,
                  border: `1px solid linear-gradient(151.477deg, rgb(0, 0, 0) 17.606%, rgba(${hexToRgb(
                    primaryColor,
                  )}, 0.4) 188.68%)`,
                  boxShadow: '0px 0px 23.4px 0px rgba(0, 0, 0, 0.50)',
                }}
              >
                <h2
                  className="py-1 text-center text-3xl font-medium"
                  style={{
                    color: primaryColor,
                    textShadow: `0 0 9px rgba(${hexToRgb(primaryColor)}, 0.49)`,
                  }}
                >
                  Settings
                </h2>

                {/* Decorative Line */}
                <div className="relative flex w-full items-center justify-center">
                  <div className="w-full">
                    <div className="relative h-[1px] w-full">
                      <div
                        className="absolute inset-0"
                        style={{
                          background: `linear-gradient(90deg, transparent 0%, ${primaryColor} 48%, transparent 100%)`,
                          opacity: 0.995,
                        }}
                      />
                    </div>
                  </div>
                </div>

                <div className="mx-auto mb-2 mt-3 flex w-full max-w-md flex-col gap-8 pb-3 pt-5">
                  <div className="flex flex-col gap-10">
                    {/* Music Volume */}
                    <div className="flex flex-col items-center gap-1">
                      <label className="font-rubik mb-2 text-2xl font-normal tracking-[-0.005rem] text-white">
                        Music Volume
                      </label>
                      <Slider
                        defaultValue={[40]}
                        max={100}
                        step={1}
                        className="w-full"
                        // trackClassName="bg-white/70"
                        // thumbClassName="bg-white border border-gray-300 shadow-sm"
                      />
                    </div>
                    {/* Voice Over Volume */}
                    <div className="flex flex-col items-center gap-1">
                      <label className="font-rubik mb-2 text-2xl font-normal tracking-tight text-white">
                        Voice over volume
                      </label>
                      <Slider
                        defaultValue={[20]}
                        max={100}
                        step={1}
                        className="w-full"
                        // trackClassName="bg-white/70"
                        // thumbClassName="bg-white border border-gray-300 shadow-sm"
                      />
                    </div>
                    {/* Color Picker */}
                    <div className="flex flex-col items-center gap-1">
                      <label className="font-rubik mb-2 text-2xl font-normal tracking-[-0.005rem] text-white">
                        Set Theme
                      </label>
                      <ColorPicker />
                    </div>
                  </div>

                  {/* Okay button */}
                  <DialogTrigger asChild>
                    <Buttons
                      variant="ghost"
                      size="sm"
                      className="mx-auto h-11 w-[5.75rem] rounded-3xl px-4 py-2 text-lg text-white transition-all duration-200 hover:scale-110 hover:text-white"
                      style={{
                        background: `linear-gradient(275.041deg
                              ,
                                rgba(${hexToRgb(
                                  primaryColor,
                                )}, 0.7) 7.25%, rgba(0, 0, 0, 0.8) 84.803%)`,
                        border: `1px solid ${primaryColor}`,
                        boxShadow: `0px 0px 15.9542px 0px rgba(3, 51, 38, 0.8)`,
                      }}
                    >
                      Okay
                    </Buttons>
                  </DialogTrigger>
                </div>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        {/* Main Loading Content */}
        <div className="relative mt-auto flex h-fit flex-grow translate-y-[6.25rem] flex-col items-center justify-center">
          {/* Background Union Shape */}
          <div className="pointer-events-none absolute left-0 top-1/2 flex h-[26.128rem] w-full -translate-y-1/2 items-center justify-center">
            <div className="w-full flex-none scale-y-[-100%]">
              <div className="relative h-[26.128rem] w-full">
                <div className="absolute bottom-[-6.629%] left-[-1.734%] right-[-1.734%] top-[-4.8%]">
                  <svg
                    className="block h-full w-full"
                    fill="none"
                    preserveAspectRatio="none"
                    viewBox="0 0 1426 467"
                  >
                    <g filter="url(#filter0_di_loading)" id="Union">
                      <mask fill="white" id="path-1-inside-1_loading">
                        <path d={svgPaths.p17e7b480} />
                      </mask>
                      <path d={svgPaths.p17e7b480} fill="#171717" />
                      <path
                        d={svgPaths.p1856c00}
                        fill="url(#paint0_linear_loading)"
                        mask="url(#path-1-inside-1_loading)"
                      />
                    </g>
                    <defs>
                      <filter
                        colorInterpolationFilters="sRGB"
                        filterUnits="userSpaceOnUse"
                        height="465.826"
                        id="filter0_di_loading"
                        width="1425.67"
                        x="0.111481"
                        y="0.933644"
                      >
                        <feFlood floodOpacity="0" result="BackgroundImageFix" />
                        <feColorMatrix
                          in="SourceAlpha"
                          result="hardAlpha"
                          type="matrix"
                          values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
                        />
                        <feOffset dy="3.82216" />
                        <feGaussianBlur stdDeviation="11.9443" />
                        <feComposite in2="hardAlpha" operator="out" />
                        <feColorMatrix
                          type="matrix"
                          values="0 0 0 0 0 0 0 0 0 1 0 0 0 0 0.733333 0 0 0 0.3 0"
                        />
                        <feBlend
                          in2="BackgroundImageFix"
                          mode="normal"
                          result="effect1_dropShadow_loading"
                        />
                        <feBlend
                          in="SourceGraphic"
                          in2="effect1_dropShadow_loading"
                          mode="normal"
                          result="shape"
                        />
                        <feColorMatrix
                          in="SourceAlpha"
                          result="hardAlpha"
                          type="matrix"
                          values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
                        />
                        <feOffset dy="3.82216" />
                        <feGaussianBlur stdDeviation="7.35766" />
                        <feComposite
                          in2="hardAlpha"
                          k2="-1"
                          k3="1"
                          operator="arithmetic"
                        />
                        <feColorMatrix
                          type="matrix"
                          values="0 0 0 0 1 0 0 0 0 1 0 0 0 0 1 0 0 0 0.1 0"
                        />
                        <feBlend
                          in2="shape"
                          mode="normal"
                          result="effect2_innerShadow_loading"
                        />
                      </filter>
                      <linearGradient
                        gradientUnits="userSpaceOnUse"
                        id="paint0_linear_loading"
                        x1="447.966"
                        x2="522.786"
                        y1="163.137"
                        y2="535.758"
                      >
                        <stop stopColor="#033326" />
                        <stop offset="1" stopColor={primaryColor} />
                      </linearGradient>
                    </defs>
                  </svg>
                </div>
              </div>
            </div>
          </div>

          {/* Loading Content Container */}
          <div className="relative z-10 h-[25rem] w-full max-w-6xl">
            {/* Animated Loading Groups */}
            <LoadingGroup1 delay={0} progress={progress} />
            {/* Progress Text */}
            <div className="absolute bottom-[18.25rem] left-1/2 z-20 -translate-x-[10%]">
              <span
                className="font-rubik text-sm font-medium tracking-wider"
                style={{ color: primaryColor }}
              >
                {progress.toFixed(0)}%
              </span>
            </div>
            <LoadingGroup2 delay={200} />
            <LoadingGroup3 delay={400} />
            <LoadingGroup4 delay={600} />
            <LoadingGroup5 delay={800} />

            {/* Central Text */}
            <div
              className="absolute left-1/2 top-1/2 z-20 flex  translate-x-[-45%] flex-col justify-center whitespace-normal text-center font-['Lato:Regular',_sans-serif] text-[2rem] not-italic tracking-[0.08px] text-[#ffffff]"
              style={{
                textShadow: `rgba(${hexToRgb(primaryColor)}, 0.6) 0px 0px 20px`,
                filter: `drop-shadow(0 0 10px rgba(${hexToRgb(
                  primaryColor,
                )}, 0.3))`,
              }}
            >
              <p
                className="adjustLetterSpacing block animate-pulse whitespace-pre leading-[normal]"
                style={{ marginTop: '-15px' }}
              >
                Your learning adventure awaits
              </p>
              {!isStoryScreen && (
                <p
                  style={{ color: primaryColor }}
                  className="m-4 mx-auto max-w-md  animate-pulse text-center !text-base leading-normal"
                >
                  The game may take a few minutes to load the first time. You
                  can switch tabs and return once it’s ready.
                </p>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Custom CSS for enhanced animations */}
      <style>{`
        @keyframes loadingPulse {
          0%,
          100% {
            opacity: 0.3;
            transform: scale(1);
          }
          50% {
            opacity: 1;
            transform: scale(1.05);
          }
        }

        @keyframes loadingGlow {
          0%,
          100% {
            filter: drop-shadow(0 0 5px rgba(${hexToRgb(primaryColor)}, 0.3));
          }
          50% {
            filter: drop-shadow(0 0 20px rgba(${hexToRgb(primaryColor)}, 0.8));
          }
        }

        .animate-pulse {
          animation: loadingPulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
        }
      `}</style>
    </div>
  );
};

export default LoadingScreen;
